import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { shouldRefreshOnResume } from '../../utils/timeUtils';
import { DEFAULT_PORTFOLIO_PROFILES, normalizeProfiles } from '../../components/PortfolioProfileToggle';
import { API_BASE_URL as API_BASE } from '../../apiBase';
import {
  cacheBustBucket, CALL_BUCKETS, PUT_BUCKETS, ASSESSMENT_PERIODS, IC_PERIODS,
} from './shared';

// Re-exported so existing `from './assessment/AssessmentDataContext'` imports
// (Assessment.js) keep working -- these live in shared.js to avoid a
// context->shared->context import cycle (BucketPill/WinRateTable need them
// too, and shared.js is imported by AssessmentDataContext for cacheBustBucket).
export { CALL_BUCKETS, PUT_BUCKETS, ASSESSMENT_PERIODS, IC_PERIODS };

// ── Constants ─────────────────────────────────────────────────────────────────
// Moved verbatim out of Assessment.js (Tech Debt 2026-09, "Assessment and
// Backtest are god files" — remaining-7-tabs context lift). These drive the
// shared /api/assessment fetch and the derived state every tab consumes.

export const TABS = [
  { id: 'monte_carlo', label: 'Monte Carlo' },
  { id: 'win_rate',    label: 'Win Rates' },
  { id: 'take_profit', label: 'Take Profit %' },
  { id: 'windows',     label: 'Windows' },
  { id: 'returns',     label: 'Returns & Peak' },
  { id: 'excursion',   label: 'Excursion' },
  { id: 'ic',          label: 'IC Bands' },
  { id: 'pearson',     label: 'Pearson' },
  { id: 'calendar',    label: 'Calendar' },
];
export const TAB_IDS = TABS.map(t => t.id);

// DTE strategy variants — the dashboard toggle switches the "primary" period anchors.
// 30 DTE: hard-sell at bar 15, full hold ends at 30 cal days. Primary periods: 15d, 30d.
// 15 DTE: hard-sell at bar 7,  full hold ends at 15 cal days. Primary periods: 7d, 15d.
export const DTE_STRATEGIES = {
  '30': { label: '30 DTE', hardPeriod: '15d', holdPeriod: '30d', primaries: ['15d', '30d'] },
  '15': { label: '15 DTE', hardPeriod: '7d',  holdPeriod: '15d', primaries: ['7d', '15d'] },
};
export const DTE_STRATEGY_STORAGE_KEY = 'assessmentDteStrategyPrimary20260515';
export const PORTFOLIO_PROFILE_STORAGE_KEY = 'assessmentPortfolioProfile20260523';
// TP% anchor variant (2026-08-10 dual-anchor reanchor — experiments/assess_reanchor_2026_08/).
// 'tp' = frozen Phase H5/15B anchors (feeds W1-W6 ship gates, kept byte-identical).
// 'tp26' = live 2026-08-10 TP/SL retune canon, 30 DTE only, measurement-only (additive).
export const TP_ANCHOR_STORAGE_KEY = 'assessmentTpAnchor20260810';
export const TP_ANCHORS = {
  tp:   { label: 'Legacy',     title: 'Frozen Phase H5 (30 DTE) / 15B (15 DTE) anchors — feeds the W1-W6 ship gates' },
  tp26: { label: '2026 Canon', title: 'Live 2026-08-10 TP/SL retune canon (calls TP+10%/SL-100%), 30 DTE only — measurement instrument, gates still read Legacy' },
};

// ── Derived-state helpers (private to this module) ───────────────────────────

function bucketsForSelectedWindow(dataset, selectedWindow) {
  if (!dataset) return [];
  if (selectedWindow) {
    const win = (dataset.windows || []).find(w => w.label === selectedWindow);
    if (win) return win.buckets || [];
  }
  return dataset.buckets || [];
}

function bucketMapForSelectedWindow(dataset, selectedWindow) {
  const map = {};
  bucketsForSelectedWindow(dataset, selectedWindow).forEach(b => { map[b.bucket] = b; });
  return map;
}

function pearsonFromPairs(pairs) {
  if (pairs.length < 3) return null;
  const n = pairs.length;
  const meanX = pairs.reduce((sum, [x]) => sum + x, 0) / n;
  const meanY = pairs.reduce((sum, [, y]) => sum + y, 0) / n;
  let cov = 0;
  let varX = 0;
  let varY = 0;
  pairs.forEach(([x, y]) => {
    const dx = x - meanX;
    const dy = y - meanY;
    cov += dx * dy;
    varX += dx * dx;
    varY += dy * dy;
  });
  const denom = Math.sqrt(varX * varY);
  if (!denom) return null;
  return Math.round((cov / denom) * 1000) / 1000;
}

function computeRtrCorrelationsFromBuckets(buckets) {
  const byBucket = {};
  (buckets || []).forEach(b => { byBucket[b.bucket] = b; });
  const result = {};
  ASSESSMENT_PERIODS.forEach(p => {
    const highPairs = [95, 90, 85, 80, 75, 70]
      .map(t => [t, byBucket[`${t}+`]?.rtr_win_rate?.[p]])
      .filter(([, v]) => v != null);
    const lowPairs = [30, 25, 20, 15, 10, 5]
      .map(t => [50 - t, byBucket[`<${t}`]?.rtr_win_rate?.[p]])
      .filter(([, v]) => v != null);
    result[p] = {
      high: pearsonFromPairs(highPairs),
      low: pearsonFromPairs(lowPairs),
    };
  });
  return result;
}

// ── Context ────────────────────────────────────────────────────────────────

const AssessmentDataCtx = createContext(null);

export function AssessmentDataProvider({ children }) {
  const { tab: tabParam } = useParams();
  const navigate = useNavigate();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  // Take Profit % data (Phase 17, 2026-04-29) — separate fetch so toggling
  // tabs doesn't refetch the WR data. Cached per (dte, versionId) so toggling
  // DTE on the TP% tab swaps instantly without a loading flash.
  const [tpCache, setTpCache]     = useState({});  // { 'dte_versionId': data }
  const [tpLoading, setTpLoading] = useState(false);
  const [tpError, setTpError]     = useState(null);
  const activeTab = TAB_IDS.includes(tabParam) ? tabParam : 'win_rate';
  const setActiveTab = (id) => navigate(id === 'win_rate' ? '/assessment' : `/assessment/${id}`);
  const [showCalls, setShowCalls] = useState(true);
  const [showPuts, setShowPuts]   = useState(true);
  const [showScaled, setShowScaled] = useState(true);
  const [dteStrategy, setDteStrategy] = useState(() => {
    if (typeof window === 'undefined') return '30';
    return window.localStorage?.getItem(DTE_STRATEGY_STORAGE_KEY) === '15' ? '15' : '30';
  });
  const [portfolioProfile, setPortfolioProfile] = useState(() => {
    if (typeof window === 'undefined') return 'core';
    const stored = window.localStorage?.getItem(PORTFOLIO_PROFILE_STORAGE_KEY);
    return ['sentinel', 'core', 'apex'].includes(stored) ? stored : 'core';
  });
  const [portfolioProfiles, setPortfolioProfiles] = useState(DEFAULT_PORTFOLIO_PROFILES);
  const dteCfg = DTE_STRATEGIES[dteStrategy] || DTE_STRATEGIES['30'];
  const setDteStrategyPersist = (val) => {
    setDteStrategy(val);
    try { window.localStorage?.setItem(DTE_STRATEGY_STORAGE_KEY, val); } catch (e) { /* ignore */ }
  };
  // TP% anchor variant — default 'tp' (legacy) so existing users see no UI change.
  const [tpAnchor, setTpAnchor] = useState(() => {
    if (typeof window === 'undefined') return 'tp';
    return window.localStorage?.getItem(TP_ANCHOR_STORAGE_KEY) === 'tp26' ? 'tp26' : 'tp';
  });
  const setTpAnchorPersist = (val) => {
    setTpAnchor(val);
    try { window.localStorage?.setItem(TP_ANCHOR_STORAGE_KEY, val); } catch (e) { /* ignore */ }
  };
  const setPortfolioProfilePersist = (val) => {
    setPortfolioProfile(val);
    try { window.localStorage?.setItem(PORTFOLIO_PROFILE_STORAGE_KEY, val); } catch (e) { /* ignore */ }
  };
  const updatePortfolioProfiles = useCallback((profiles) => {
    setPortfolioProfiles(normalizeProfiles(profiles));
  }, []);
  const [selectedWindow, setSelectedWindow] = useState(null); // set once data loads → widest window
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [availableVersions, setAvailableVersions] = useState([]);
  // tpCacheKey depends on selectedVersionId + dteStrategy + tpAnchor — must be
  // declared AFTER all three states are initialized to avoid a temporal-dead-zone
  // error. tpAnchor MUST be in this key: two anchors can be fetched at the same
  // (dte, version) and must not read each other's cached response.
  const tpCacheKey = `${dteStrategy}_${tpAnchor}_${selectedVersionId ?? ''}`;
  const tpData = tpCache[tpCacheKey] || null;
  const [versionMenuOpen, setVersionMenuOpen] = useState(false);
  const [activeData, setActiveData] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const assessmentUpdatedRef = useRef(null);
  const lastAssessmentResumeRefreshRef = useRef(0);
  const versionMenuRef = useRef(null);

  assessmentUpdatedRef.current = data?.version?.updated_at || null;

  useEffect(() => {
    if (!versionMenuOpen) return;
    const onDocClick = (e) => {
      if (versionMenuRef.current && !versionMenuRef.current.contains(e.target)) {
        setVersionMenuOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setVersionMenuOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [versionMenuOpen]);

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/portfolio/profiles/compare?_t=${cacheBustBucket()}`)
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (!alive || !Array.isArray(j?.rows)) return;
        setPortfolioProfiles(normalizeProfiles(j.rows));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const refreshAssessmentIfStale = useCallback(() => {
    const now = Date.now();
    if (now - lastAssessmentResumeRefreshRef.current < 60000) return;
    if (!shouldRefreshOnResume(assessmentUpdatedRef.current)) return;

    lastAssessmentResumeRefreshRef.current = now;
    setTpCache({});
    setRefreshNonce(n => n + 1);
  }, []);

  useEffect(() => {
    const onResume = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      refreshAssessmentIfStale();
    };
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    window.addEventListener('pageshow', onResume);
    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('pageshow', onResume);
    };
  }, [refreshAssessmentIfStale]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (selectedVersionId) params.set('version', selectedVersionId);
    if (dteStrategy === '15') params.set('dte', '15');
    params.set('_t', cacheBustBucket());
    const url = `${API_BASE}/api/assessment${params.toString() ? '?' + params.toString() : ''}`;
    fetch(url)
      .then(r => {
        if (!r.ok) return r.json().then(e => {
          if (e.available_versions) setAvailableVersions(e.available_versions);
          throw new Error(e.error || `HTTP ${r.status}`);
        });
        return r.json();
      })
      .then(d => {
        setData(d);
        if (d?.available_versions) setAvailableVersions(d.available_versions);
        if (selectedVersionId == null && d?.version?.id) setSelectedVersionId(d.version.id);
        // Default to 5y window only on first load; preserve the user's selection
        // across DTE toggles and version switches.
        if (d?.windows?.length) {
          setSelectedWindow(prev => {
            if (prev && d.windows.some(w => w.label === prev)) return prev;
            const preferred = d.windows.find(w => w.label === '5y');
            const fallback  = d.windows.reduce(
              (best, w) => (w.lookback_days > (best?.lookback_days ?? -1) ? w : best),
              null,
            );
            return (preferred ?? fallback)?.label ?? null;
          });
        }
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [selectedVersionId, dteStrategy, refreshNonce]);

  // When viewing a historical (non-active) version, fetch active version's
  // full response so each cell can show a delta vs active (incl. per-window).
  useEffect(() => {
    const activeId = data?.active_version_id;
    const currId   = data?.version?.id;
    if (!activeId || !currId || activeId === currId) {
      setActiveData(null);
      return;
    }
    let cancelled = false;
    const params = new URLSearchParams();
    if (dteStrategy === '15') params.set('dte', '15');
    params.set('_t', cacheBustBucket());
    fetch(`${API_BASE}/api/assessment?${params.toString()}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d) setActiveData(d); })
      .catch(() => { if (!cancelled) setActiveData(null); });
    return () => { cancelled = true; };
  }, [data, dteStrategy, refreshNonce]);

  // Fetch TP% data only when the user actually opens the tab (lazy).
  // Cache by (dte, versionId) so toggling DTE shows cached data immediately
  // (no loading flash) while a refetch confirms in the background.
  useEffect(() => {
    if (activeTab !== 'take_profit') return;
    if (tpCache[tpCacheKey]) return;  // already cached for this combo
    setTpLoading(true);
    setTpError(null);
    const params = new URLSearchParams();
    params.set('metric', tpAnchor);
    if (selectedVersionId) params.set('version', selectedVersionId);
    if (dteStrategy === '15') params.set('dte', '15');
    params.set('_t', cacheBustBucket());
    const keyAtFetch = tpCacheKey;
    fetch(`${API_BASE}/api/assessment?${params.toString()}`)
      .then(r => {
        if (!r.ok) return r.json().then(e => { throw new Error(e.error || `HTTP ${r.status}`); });
        return r.json();
      })
      .then(d => {
        setTpCache(prev => ({ ...prev, [keyAtFetch]: d }));
        setTpLoading(false);
      })
      .catch(e => { setTpError(e.message); setTpLoading(false); });
  }, [activeTab, dteStrategy, tpAnchor, selectedVersionId, tpCacheKey, tpCache]);

  // TP% is a separate metric fetch, so historical TP% needs its own active
  // comparison payload instead of reusing the WR payload in activeData.
  useEffect(() => {
    if (activeTab !== 'take_profit') return;
    const activeId = data?.active_version_id;
    const currId = data?.version?.id;
    if (!activeId || !currId || activeId === currId) return;
    const activeTpKey = `${dteStrategy}_${tpAnchor}_${activeId}`;
    if (tpCache[activeTpKey]) return;

    const params = new URLSearchParams();
    params.set('metric', tpAnchor);
    params.set('version', activeId);
    if (dteStrategy === '15') params.set('dte', '15');
    params.set('_t', cacheBustBucket());
    let cancelled = false;
    fetch(`${API_BASE}/api/assessment?${params.toString()}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!cancelled && d) setTpCache(prev => ({ ...prev, [activeTpKey]: d }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [activeTab, dteStrategy, tpAnchor, data?.active_version_id, data?.version?.id, tpCache]);

  // tp26 is 30-DTE-only (no canon values exist for 15 DTE — see PREREG §1.2) — fall
  // back to the legacy anchor rather than fetch a combo that can never be populated.
  useEffect(() => {
    if (dteStrategy === '15' && tpAnchor === 'tp26') setTpAnchorPersist('tp');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dteStrategy, tpAnchor]);

  const filteredBuckets = useMemo(() => {
    if (!data?.buckets) return [];
    return data.buckets.filter(b => {
      if (!CALL_BUCKETS.has(b.bucket) && !PUT_BUCKETS.has(b.bucket)) return false;
      if (b.side === 'call' && !showCalls) return false;
      if (b.side === 'put'  && !showPuts)  return false;
      return true;
    });
  }, [data, showCalls, showPuts]);

  // Window-selected buckets — used by every tab that respects the window
  // toggle (win_rate, take_profit, returns, excursion, pearson). Falls back
  // to aggregated meta (`filteredBuckets`) when no window is selected or the
  // selected window has no bucket rows. Take Profit % uses tpData windows;
  // every other tab uses `data.windows`.
  const winRateBuckets = useMemo(() => {
    if (!selectedWindow) return filteredBuckets;
    const win = (data?.windows || []).find(w => w.label === selectedWindow);
    if (!win) return filteredBuckets;
    return (win.buckets || []).filter(b => {
      if (!CALL_BUCKETS.has(b.bucket) && !PUT_BUCKETS.has(b.bucket)) return false;
      if (b.side === 'call' && !showCalls) return false;
      if (b.side === 'put'  && !showPuts)  return false;
      return true;
    });
  }, [data, selectedWindow, filteredBuckets, showCalls, showPuts]);

  // Generic window-selected buckets respecting both side toggles.
  // Used by Returns / Excursion / Pearson tabs.
  const selectedWindowBuckets = winRateBuckets;

  // Comparison bucket lookup: shows delta vs the ACTIVE version when a
  // historical version is selected. Empty (no deltas) when viewing active.
  // When a window is selected, compare against the SAME window on active.
  const prevByBucket = useMemo(() => {
    if (!activeData) return {};
    return bucketMapForSelectedWindow(activeData, selectedWindow);
  }, [activeData, selectedWindow]);

  const activeTpData = tpCache[`${dteStrategy}_${data?.active_version_id ?? ''}`] || null;
  const activeTpByBucket = useMemo(() => (
    bucketMapForSelectedWindow(activeTpData, selectedWindow)
  ), [activeTpData, selectedWindow]);

  const activeBandIcByBand = useMemo(() => {
    const map = {};
    (activeData?.band_ics || []).forEach(row => { map[row.band] = row; });
    return map;
  }, [activeData]);

  const selectedRtrCorrelations = useMemo(() => (
    computeRtrCorrelationsFromBuckets(selectedWindowBuckets)
  ), [selectedWindowBuckets]);

  const activeRtrCorrelations = useMemo(() => (
    computeRtrCorrelationsFromBuckets(Object.values(prevByBucket))
  ), [prevByBucket]);

  const value = {
    // routing
    activeTab, setActiveTab,
    // fetch state
    data, loading, error,
    availableVersions, selectedVersionId, setSelectedVersionId,
    activeData, refreshNonce,
    // TP% fetch state
    tpCache, tpData, tpLoading, tpError, tpCacheKey, tpAnchor, setTpAnchorPersist,
    // UI toggles shared across tabs
    showCalls, setShowCalls, showPuts, setShowPuts, showScaled, setShowScaled,
    dteStrategy, setDteStrategyPersist, dteCfg,
    portfolioProfile, setPortfolioProfilePersist, portfolioProfiles, updatePortfolioProfiles,
    selectedWindow, setSelectedWindow,
    versionMenuOpen, setVersionMenuOpen, versionMenuRef,
    // derived state
    filteredBuckets, winRateBuckets, selectedWindowBuckets,
    prevByBucket, activeTpByBucket, activeBandIcByBand,
    selectedRtrCorrelations, activeRtrCorrelations,
  };

  return (
    <AssessmentDataCtx.Provider value={value}>
      {children}
    </AssessmentDataCtx.Provider>
  );
}

export function useAssessmentData() {
  const ctx = useContext(AssessmentDataCtx);
  if (!ctx) throw new Error('useAssessmentData must be used within an AssessmentDataProvider');
  return ctx;
}
